import React, { useEffect, useState, useMemo, useCallback, useLayoutEffect, useRef } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { some, isEqual } from 'lodash';

import biService from '../../../service/bi.service';
import useUsers from '../../../context/UsersContext';
import { BiReportType } from '../../../config/bi_report';
import TableReport from './TableReport';
import PieReport from './PieReport';
import BarReport from './BarReport';
import ReportButtons from './ReportButtons';
import KPIReport from './KPIReport';

const BiReport = ({ reportId, reRender, height, dashboardData, filter, dashboardView }) => {
  const { getUser, user } = useUsers();

  const [refresh, setRefresh] = useState(false);
  const [report, setReport] = useState({});
  const [linkedReportId, setLinkedReportId] = useState(null);

  const [activeLevel, setActiveLevel] = useState(null);
  const [filterVals, setFilterVals] = useState({ filter: [] });
  const [reportStates, setReportStates] = useState([]);
  const [reportButtonsHeight, setReportButtonsHeight] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 900
  );
  const reportButtonsRef = useRef(null);

  const reportHeight = useMemo(() => {
    if (typeof height === 'number' && Number.isFinite(height) && height > 0) {
      return height;
    }

    // Keep standalone reports visible without cutting off the grid footer.
    return Math.max(360, Math.floor(viewportHeight - 280));
  }, [height, viewportHeight]);

  const contentHeight = useMemo(
    () => Math.max(180, reportHeight - reportButtonsHeight - 8),
    [reportHeight, reportButtonsHeight]
  );

  const isDashboardView = useMemo(
    () => dashboardView ?? Object.keys(dashboardData ?? {}).length > 0,
    [dashboardData, dashboardView]
  );

  const chartContentHeight = useMemo(
    () => (isDashboardView ? Math.max(150, Math.floor(contentHeight * 0.93)) : contentHeight),
    [isDashboardView, contentHeight]
  );

  const tableContentHeight = useMemo(
    () => (isDashboardView ? contentHeight : Math.max(220, contentHeight + 48)),
    [isDashboardView, contentHeight]
  );

  const getReport = useCallback(() => {
    return biService.getReport(reportId).then((data) => setReport(data));
  }, [reportId]);

  const getLinkedReport = useCallback((linkedReportId) => {
    biService.getReport(linkedReportId).then((data) => setReport(data));
  }, []);

  useEffect(() => {
    if (report?.id !== reportId) {
      getReport();
    }
    setFilterVals({ filter: [] });
    setActiveLevel(null);
    setReportStates([]);
    setLinkedReportId(null);

    if (!Object.keys(user).length) getUser();
  }, [reportId]);

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useLayoutEffect(() => {
    const recalculateButtonsHeight = () => {
      const nextHeight = reportButtonsRef.current?.offsetHeight ?? 0;
      setReportButtonsHeight(nextHeight);
    };

    recalculateButtonsHeight();
    window.addEventListener('resize', recalculateButtonsHeight);

    return () => window.removeEventListener('resize', recalculateButtonsHeight);
  }, [report?.reportType, linkedReportId, activeLevel, reportStates.length, filterVals.filter.length]);

  useEffect(() => {
    //rerender is outer refresh call

    if (reRender) {
      setRefresh(true);
    }
  }, [reRender]);

  useEffect(() => {
    if (refresh) {
      getReport();
      setRefresh(false);
    }
  }, [refresh]);

  useEffect(() => {
    if (linkedReportId) {
      getLinkedReport(linkedReportId);
    } else {
      if (Object.keys(report).length > 0 && report.id !== linkedReportId) {
        getReport();
      }
    }
  }, [linkedReportId]);

  useEffect(() => {
    const repFilter = [];
    if (Object.keys(report).length > 0 && user) {
      if (filter?.businessPartner && report.bpField) {
        repFilter.push({
          field: report.bpField,
          value: filter?.businessPartner?.value,
          isDefault: true,
        });
      }

      if (filter?.year && report.yearField) {
        repFilter.push({
          field: report.yearField,
          value: filter.year,
          isDefault: true,
        });
      }

      if (filter?.quarter && report.querterField) {
        repFilter.push({
          field: report.querterField,
          value: filter.quarter,
          isDefault: true,
        });
      }

      if (filter?.halfYear && report.halfYearField) {
        repFilter.push({
          field: report.halfYearField,
          value: filter.halfYear,
          isDefault: true,
        });
      }

      if (filter?.month && report.monthField) {
        repFilter.push({
          field: report.monthField,
          value: filter.month,
          isDefault: true,
        });
      }

      if (filter?.week && report.weekField) {
        repFilter.push({
          field: report.weekField,
          value: filter.week,
          isDefault: true,
        });
      }

      if (filter?.interval?.from && report.dateInterValField) {
        repFilter.push({
          field: report.dateInterValField,
          value: filter?.interval?.from.format('YYYY-MM-DD'),
          gte: true,
          isDefault: true,
        });
      }

      if (filter?.interval?.to && report.dateInterValField) {
        repFilter.push({
          field: report.dateInterValField,
          value: filter?.interval?.to.format('YYYY-MM-DD'),
          lte: true,
          isDefault: true,
        });
      }
      //dashboardData
      if (filter?.responsible && report.responsibleField && dashboardData?.responsibleFilter) {
        repFilter.push({
          field: report.responsibleField,
          value: filter?.responsible?.value,
          isDefault: true,
        });
      }

      if (
        Object.keys(user.userSalesPerson ?? {}).length > 0 &&
        report.responsibleField &&
        !dashboardData?.responsibleFilter
      ) {
        repFilter.push({
          field: report.responsibleField,
          value: user.userSalesPerson.slpCode,
          isDefault: true,
        });
      }
    }

    setFilterVals((prev) => {
      const previousFilters = prev?.filter ?? [];
      const previousDefaultFilters = previousFilters.filter((item) => item?.isDefault);

      if (isEqual(previousDefaultFilters, repFilter)) {
        return prev;
      }

      const drillFilters = previousFilters.filter((item) => !item?.isDefault);
      return { ...prev, filter: [...repFilter, ...drillFilters] };
    });
  }, [filter, report, dashboardData]);

  const Report = useMemo(
    () => () => {
      if (report?.reportType === BiReportType.TABLE) {
        return (
          <TableReport
            report={report}
            linkedReportIdState={{ linkedReportId, setLinkedReportId }}
            activeLevelState={{ activeLevel, setActiveLevel }}
            filterValsState={{ filterVals, setFilterVals }}
            reportStatesState={{ reportStates, setReportStates }}
            height={tableContentHeight}
            setRefresh={setRefresh}
          />
        );
      } else if (report?.reportType === BiReportType.PIE) {
        return (
          <PieReport
            report={report}
            linkedReportIdState={{ linkedReportId, setLinkedReportId }}
            activeLevelState={{ activeLevel, setActiveLevel }}
            filterValsState={{ filterVals, setFilterVals }}
            reportStatesState={{ reportStates, setReportStates }}
            height={chartContentHeight}
          />
        );
      } else if (
        report?.reportType === BiReportType.BAR ||
        report?.reportType === BiReportType.HORIZONTAL ||
        report?.reportType === BiReportType.LINE ||
        report?.reportType === BiReportType.COMBO
      ) {
        return (
          <BarReport
            report={report}
            linkedReportIdState={{ linkedReportId, setLinkedReportId }}
            activeLevelState={{ activeLevel, setActiveLevel }}
            filterValsState={{ filterVals, setFilterVals }}
            reportStatesState={{ reportStates, setReportStates }}
            height={chartContentHeight}
          />
        );
      } else if (report?.reportType === BiReportType.KPI) {
        return (
          <KPIReport
            report={report}
            linkedReportIdState={{ linkedReportId, setLinkedReportId }}
            activeLevelState={{ activeLevel, setActiveLevel }}
            filterValsState={{ filterVals, setFilterVals }}
            reportStatesState={{ reportStates, setReportStates }}
            height={contentHeight}
          />
        );
      } else {
        return (
          <div className="relative inset-0 bg-black bg-opacity-20 flex justify-center items-center h-[1200px] mt-[-50px]">
            {/*<CircularProgress size={70} />*/}
          </div>
        );
      }
    },
    [
      report,
      activeLevel,
      filterVals,
      linkedReportId,
      reportStates,
      contentHeight,
      chartContentHeight,
      tableContentHeight,
    ]
  );

  return (
    <div className="relative w-full">
      <div ref={reportButtonsRef}>
        <ReportButtons
          report={report}
          fromDashBoard={isDashboardView}
          dashboardFilter={filter}
          dashboardData={dashboardData}
          filterValsState={{ filterVals, setFilterVals }}
          activeLevelState={{ activeLevel, setActiveLevel }}
          linkedReportIdState={{ linkedReportId, setLinkedReportId }}
          reportStatesState={{ reportStates, setReportStates }}
        />
      </div>

      <Report />
    </div>
  );
};

export default BiReport;
